export interface DomRefs {
  root: ParentNode;
  host: HTMLElement | null;
  shell: HTMLElement;
  appTitle: HTMLElement | null;
  form: HTMLFormElement;
  marketplace: HTMLSelectElement;
  category: HTMLSelectElement;
  price: HTMLInputElement;
  cogs: HTMLInputElement;
  weight: HTMLInputElement;
  length: HTMLInputElement;
  width: HTMLInputElement;
  height: HTMLInputElement;
  inbound: HTMLInputElement;
  duties: HTMLInputElement;
  prep: HTMLInputElement;
  inboundPlacementMode: HTMLSelectElement;
  inboundPlacementManual: HTMLInputElement;
  inboundPlacementManualWrap: HTMLDivElement;
  calcWarning: HTMLDivElement;
  results: HTMLDivElement;
  advancedToggle: HTMLButtonElement;
  advancedFields: HTMLDivElement;
  advancedIcon: HTMLSpanElement;
  sliderContainer: HTMLDivElement;
  priceSlider: HTMLInputElement;
  sliderValue: HTMLSpanElement;
  weightUnitLabel: HTMLSpanElement;
  dimensionUnitLabel: HTMLSpanElement;
}

function queryById<T extends HTMLElement>(root: ParentNode, id: string): T | null {
  if ('getElementById' in root && typeof (root as Document).getElementById === 'function') {
    return (root as Document).getElementById(id) as T | null;
  }

  return (root as ParentNode).querySelector<T>(`#${id}`);
}

function requireElement<T extends HTMLElement>(root: ParentNode, id: string): T {
  const element = queryById<T>(root, id);
  if (!element) {
    throw new Error(`Missing required element: ${id}`);
  }
  return element;
}

function optionalElement<T extends HTMLElement>(root: ParentNode, id: string): T | null {
  return queryById<T>(root, id);
}

export function setHidden(element: HTMLElement | null | undefined, hidden: boolean): void {
  if (!element) return;

  if (hidden) {
    element.setAttribute('hidden', '');
    element.setAttribute('aria-hidden', 'true');
    element.style.display = 'none';
    element.classList.add('hidden');
    return;
  }

  element.removeAttribute('hidden');
  element.removeAttribute('aria-hidden');
  element.style.removeProperty('display');
  element.classList.remove('hidden');
}

export function getDomRefs(root: ParentNode): DomRefs {
  return {
    root,
    host: optionalElement<HTMLElement>(root, 'apc-v3-host'),
    shell: requireElement<HTMLElement>(root, 'apc-v3-shell'),
    appTitle: optionalElement<HTMLElement>(root, 'app-title'),
    form: requireElement<HTMLFormElement>(root, 'calc-form'),
    marketplace: requireElement<HTMLSelectElement>(root, 'marketplace'),
    category: requireElement<HTMLSelectElement>(root, 'category'),
    price: requireElement<HTMLInputElement>(root, 'price'),
    cogs: requireElement<HTMLInputElement>(root, 'cogs'),
    weight: requireElement<HTMLInputElement>(root, 'weight'),
    length: requireElement<HTMLInputElement>(root, 'length'),
    width: requireElement<HTMLInputElement>(root, 'width'),
    height: requireElement<HTMLInputElement>(root, 'height'),
    inbound: requireElement<HTMLInputElement>(root, 'inbound'),
    duties: requireElement<HTMLInputElement>(root, 'duties'),
    prep: requireElement<HTMLInputElement>(root, 'prep'),
    inboundPlacementMode: requireElement<HTMLSelectElement>(root, 'inbound-placement-mode'),
    inboundPlacementManual: requireElement<HTMLInputElement>(root, 'inbound-placement-manual'),
    inboundPlacementManualWrap: requireElement<HTMLDivElement>(root, 'inbound-placement-manual-wrap'),
    calcWarning: requireElement<HTMLDivElement>(root, 'calc-warning'),
    results: requireElement<HTMLDivElement>(root, 'results'),
    advancedToggle: requireElement<HTMLButtonElement>(root, 'toggle-advanced'),
    advancedFields: requireElement<HTMLDivElement>(root, 'advanced-fields'),
    advancedIcon: requireElement<HTMLSpanElement>(root, 'advanced-icon'),
    sliderContainer: requireElement<HTMLDivElement>(root, 'slider-container'),
    priceSlider: requireElement<HTMLInputElement>(root, 'price-slider'),
    sliderValue: requireElement<HTMLSpanElement>(root, 'slider-value'),
    weightUnitLabel: requireElement<HTMLSpanElement>(root, 'weight-unit-label'),
    dimensionUnitLabel: requireElement<HTMLSpanElement>(root, 'dimension-unit-label')
  };
}

export function findElementById<T extends HTMLElement>(root: ParentNode, id: string): T | null {
  return queryById<T>(root, id);
}

export function numberValue(input: HTMLInputElement): number {
  const value = Number(input.value);
  return Number.isFinite(value) ? value : 0;
}
